import { useState, useRef, useEffect } from 'react';
import { useStore, type ChatProvider, type ChatMessage, type ChatMessageImage, type ChatMessageVideo } from '../services/store';
import { streamChat } from '../services/api';
import type { FlowkitSkillProvenanceData } from '../services/flowkitSkills';
import { FlowkitSkillProvenance } from './FlowkitSkillProvenance';
import {
  Send, Trash2, Bot, User, Sparkles, Image, Video, Search, Code,
  Wand2, BrainCircuit, CheckCircle2, AlertTriangle, ChevronDown,
  ChevronUp, Download, Paperclip, Music, Globe, Lightbulb, Zap, X,
  StopCircle
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import GeminiAuthDialog from './GeminiAuthDialog';

// ─── Provider config ─────────────────────────────────────────────────────────

const providers: { id: ChatProvider; name: string; icon: any; accent: string }[] = [
  { id: 'claude', name: 'Claude', icon: BrainCircuit, accent: '#dc553c' },
  { id: 'gemini', name: 'Gemini', icon: Sparkles,     accent: '#4285f4' },
];

// ─── Gemini models ────────────────────────────────────────────────────────────

const GEMINI_MODELS = [
  { id: '',               label: 'Auto',  icon: Zap       },
  { id: 'gemini-3-flash', label: 'Flash', icon: Zap       },
  { id: 'gemini-3-pro',   label: 'Pro',   icon: Sparkles  },
  { id: 'gemini-3-flash-thinking', label: 'Think', icon: Lightbulb },
];

// ─── Feature actions ──────────────────────────────────────────────────────────

interface FeatureAction {
  id: string;
  icon: any;
  label: string;
  prefix?: string;
  geminiOnly?: boolean;
  color?: string;
}

const FEATURE_ACTIONS: FeatureAction[] = [
  { id: 'image_gen',     icon: Image,    label: 'Image',    prefix: '[Generate Image] ', geminiOnly: true, color: '#4285f4' },
  { id: 'video_gen',     icon: Video,    label: 'Video',    prefix: '[Generate Video] ', geminiOnly: true, color: '#ea4335' },
  { id: 'deep_research', icon: Search,   label: 'Research', prefix: '[Deep Research] ',  geminiOnly: true, color: '#34a853' },
  { id: 'web_search',    icon: Globe,    label: 'Search',   prefix: '[Search] ',         geminiOnly: true, color: '#fbbc05' },
  { id: 'music',         icon: Music,    label: 'Music',    prefix: '[Generate Music] ', geminiOnly: true, color: '#9c27b0' },
  { id: 'code',          icon: Code,     label: 'Code',     prefix: '' },
  { id: 'artifacts',     icon: Wand2,    label: 'Artifact', prefix: '[Artifact] ' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ThoughtsBlock({ thoughts }: { thoughts: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3 rounded-xl border border-[#4285f4]/20 bg-[#4285f4]/5 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-[#4285f4]/80 hover:text-[#4285f4] transition-colors"
      >
        <Lightbulb className="w-3.5 h-3.5" />
        <span>Thinking…</span>
        <span className="flex-1" />
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="border-t border-[#4285f4]/10 px-3 py-2 text-xs text-[#5f6368] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
          {thoughts}
        </div>
      )}
    </div>
  );
}

function ImageGrid({ images }: { images: ChatMessageImage[] }) {
  if (!images.length) return null;
  return (
    <div className={`grid gap-2 mt-3 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
      {images.map((img, i) => {
        const src = img.b64
          ? `data:${img.mime || 'image/png'};base64,${img.b64}`
          : img.url || '';
        if (!src) return null;
        return (
          <div key={i} className="group relative rounded-xl overflow-hidden border border-[#d6bd7c]/30 bg-[#f8f4ec]">
            <img
              src={src}
              alt={img.alt || 'Generated image'}
              className="w-full h-auto object-cover max-h-80"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-end justify-end p-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/30 to-transparent">
              <a
                href={src}
                download={`gemini-image-${i + 1}.${img.mime?.split('/')[1] || 'png'}`}
                className="rounded-lg bg-white/90 p-1.5 text-[#2f2414] hover:bg-white transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
            </div>
            {img.isGenerated && (
              <div className="absolute top-2 left-2 rounded-full bg-[#4285f4]/90 px-2 py-0.5 text-[10px] font-bold text-white">
                AI Generated
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function VideoBlock({ videos }: { videos: ChatMessageVideo[] }) {
  if (!videos.length) return null;
  return (
    <div className="mt-2 space-y-2">
      {videos.map((v, i) => (
        <a
          key={i}
          href={v.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-xl border border-[#d6bd7c]/50 bg-[#f8f4ec] px-4 py-3 hover:bg-[#fff8ec] transition-colors"
        >
          <Video className="w-5 h-5 text-[#ea4335] flex-shrink-0" />
          <span className="text-sm font-medium text-[#2f2414] truncate">{v.title}</span>
          <Download className="w-4 h-4 text-[#6d5b3f] ml-auto flex-shrink-0" />
        </a>
      ))}
    </div>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  const isEmpty = !msg.content && !msg.images?.length && !msg.thoughts && !msg.videos?.length;
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-[#d6bd7c] bg-[#fff8ec]">
          <Bot className="h-4 w-4 text-[#865916]" />
        </div>
      )}
      <div className={`max-w-[85%] rounded-2xl border px-4 py-3 shadow-sm ${
        isUser
          ? 'border-[#865916] bg-[#865916] text-[#fff8ec] rounded-br-sm'
          : 'border-[#d6bd7c] bg-[#fff8ec]/95 text-[#2f2414] rounded-bl-sm'
      }`}>
        {msg.thoughts && <ThoughtsBlock thoughts={msg.thoughts} />}
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none leading-relaxed
            [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold
            [&_pre]:rounded-lg [&_pre]:bg-black/5 [&_pre]:p-3 [&_pre]:overflow-x-auto
            [&_code]:text-xs [&_code]:bg-black/5 [&_code]:rounded [&_code]:px-1
            [&_a]:text-[#4285f4] [&_a]:underline
            [&_blockquote]:border-l-2 [&_blockquote]:border-[#d6bd7c] [&_blockquote]:pl-3 [&_blockquote]:text-[#6d5b3f]
            [&_img]:rounded-lg [&_img]:max-w-full">
            <ReactMarkdown>{isEmpty ? '●' : (msg.content || '')}</ReactMarkdown>
          </div>
        )}
        {msg.images && msg.images.length > 0 && <ImageGrid images={msg.images} />}
        {msg.videos && msg.videos.length > 0 && <VideoBlock videos={msg.videos} />}
      </div>
      {isUser && (
        <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2f2414]">
          <User className="h-4 w-4 text-[#fff8ec]" />
        </div>
      )}
    </div>
  );
}

function WelcomeScreen({ provider, onAction }: { provider: typeof providers[0]; onAction: (id: string) => void }) {
  const features = FEATURE_ACTIONS.filter(f => !f.geminiOnly || provider.id === 'gemini');
  return (
    <div className="flex h-full flex-col items-center justify-center text-center px-6">
      <div className="mb-5 rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-5 shadow-sm">
        <provider.icon className="h-12 w-12 mx-auto" style={{ color: provider.accent }} />
      </div>
      <h2 className="text-2xl font-black text-[#2f2414]">{provider.name}</h2>
      <p className="mt-1 text-sm font-medium text-[#6d5b3f] max-w-sm">
        {provider.id === 'gemini'
          ? 'Image & video generation · Deep research · Web search · Music · Thinking models'
          : 'Text-only drafting · Code explanations · Artifact drafts · No codebase or tool access'}
      </p>
      <div className="mt-5 flex flex-wrap gap-2 justify-center max-w-md">
        {features.map(f => (
          <button
            key={f.id}
            onClick={() => onAction(f.id)}
            className="flex items-center gap-1.5 rounded-full border border-[#d6bd7c] bg-[#fff8ec] px-3 py-1.5 text-xs font-semibold text-[#2f2414] transition-colors hover:bg-[#fef3d0] hover:border-[#c8942e]"
          >
            <f.icon className="w-3.5 h-3.5" style={{ color: f.color || '#865916' }} />
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function markIncompleteResponse(provider: ChatProvider, id: string, reason: string) {
  useStore.setState(state => ({
    chatMessages: {
      ...state.chatMessages,
      [provider]: state.chatMessages[provider].map(message => message.id === id
        ? { ...message, content: `${message.content}\n\n**Incomplete response:** ${reason}`.trim() }
        : message),
    },
  }));
}

export default function ChatInterface() {
  const {
    activeProvider, setActiveProvider,
    chatMessages, addMessage, updateLastAssistant,
    appendImageToLastAssistant, appendThoughtsToLastAssistant, appendVideoToLastAssistant,
    clearChat, geminiAuthStatus, setGeminiAuthStatus
  } = useStore();

  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showGeminiAuth, setShowGeminiAuth] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; mime: string; b64: string; preview?: string }[]>([]);
  const [geminiMode, setGeminiMode] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);
  const requestRef = useRef<{ provider: ChatProvider; messageId: string } | null>(null);
  const [provenanceByMessage, setProvenanceByMessage] = useState<Record<string, FlowkitSkillProvenanceData>>({});
  const [streamFeedback, setStreamFeedback] = useState<{ messageId: string; text: string; error?: boolean } | null>(null);

  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;
    const MAX_MB = 20;
    const readers = incoming.map(file => new Promise<{ name: string; mime: string; b64: string; preview?: string } | null>(resolve => {
      if (file.size > MAX_MB * 1024 * 1024) { alert(`"${file.name}" exceeds ${MAX_MB} MB limit, skipped.`); resolve(null); return; }
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        resolve({ name: file.name, mime: file.type, b64: dataUrl.split(',')[1] ?? '', preview: file.type.startsWith('image/') ? dataUrl : undefined });
      };
      reader.readAsDataURL(file);
    }));
    Promise.all(readers).then(results => {
      const valid = results.filter(Boolean) as typeof attachedFiles;
      setAttachedFiles(prev => [...prev, ...valid]);
    });
    e.target.value = '';
  };

  const removeAttachment = (idx: number) => setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  const currentProvider = providers.find(p => p.id === activeProvider)!;
  const messages = chatMessages[activeProvider];
  const isGemini = activeProvider === 'gemini';

  useEffect(() => {
    if (activeProvider === 'gemini' && geminiAuthStatus === 'unknown') {
      fetch('/api/gemini/status', {
        headers: { Authorization: `Bearer ${localStorage.getItem('studio-token') || ''}` }
      })
        .then(r => r.json())
        .then(d => { setGeminiAuthStatus(d.configured ? 'ok' : 'missing'); setGeminiMode(d.mode || ''); })
        .catch(() => setGeminiAuthStatus('missing'));
    }
  }, [activeProvider]);

  useEffect(() => {
    const container = messagesRef.current;
    if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 120) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const raw = input.trim();
    if ((!raw && !attachedFiles.length) || isStreaming) return;

    const feature = FEATURE_ACTIONS.find(f => f.id === activeMode);
    const prefix = feature?.prefix || '';
    const effectivePrompt = prefix + (raw || (attachedFiles.length ? 'Describe these images.' : ''));

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(), role: 'user', content: effectivePrompt,
      provider: activeProvider, timestamp: Date.now(),
    };
    // Show image previews in user bubble
    const previewImages = attachedFiles
      .filter(f => f.preview)
      .map(f => ({ b64: f.b64, mime: f.mime, alt: f.name, isGenerated: false }));
    const userMsgFinal = previewImages.length ? { ...userMsg, images: previewImages } : userMsg;
    addMessage(activeProvider, userMsgFinal);
    setInput('');
    setActiveMode(null);
    const filesToSend = attachedFiles.length
      ? attachedFiles.map(f => ({ name: f.name, mime: f.mime, b64: f.b64 }))
      : undefined;
    setAttachedFiles([]);
    setIsStreaming(true);

    const assistantMsg: ChatMessage = {
      id: crypto.randomUUID(), role: 'assistant', content: '',
      provider: activeProvider, timestamp: Date.now(),
    };
    addMessage(activeProvider, assistantMsg);

    let fullContent = '';
    const apiMessages = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
    const controller = new AbortController();
    abortRef.current = controller;
    requestRef.current = { provider: activeProvider, messageId: assistantMsg.id };
    setStreamFeedback({ messageId: assistantMsg.id, text: 'Request in progress…' });
    const current = () => {
      const history = useStore.getState().chatMessages[activeProvider];
      return !controller.signal.aborted && abortRef.current === controller && history[history.length - 1]?.id === assistantMsg.id;
    };

    await streamChat(
      activeProvider,
      apiMessages,
      (token) => {
        if (!current()) return;
        fullContent += token;
        updateLastAssistant(activeProvider, fullContent);
      },
      () => {
        if (!current()) return;
        setIsStreaming(false);
        setStreamFeedback(null);
        abortRef.current = null;
        requestRef.current = null;
      },
      (err) => {
        if (!current()) return;
        markIncompleteResponse(activeProvider, assistantMsg.id, err);
        setStreamFeedback({ messageId: assistantMsg.id, text: err, error: true });
        setIsStreaming(false);
        abortRef.current = null;
        requestRef.current = null;
      },
      {
        onImage: data => { if (current()) appendImageToLastAssistant(activeProvider, data); },
        onThoughts: text => { if (current()) appendThoughtsToLastAssistant(activeProvider, text); },
        onVideo: data => { if (current()) appendVideoToLastAssistant(activeProvider, data); },
        onSkills: data => {
          if (current()) setProvenanceByMessage(previous => ({ ...previous, [assistantMsg.id]: data }));
        },
        onStatus: text => { if (current()) setStreamFeedback({ messageId: assistantMsg.id, text }); },
        signal: controller.signal,
        attachedFiles: filesToSend,
      }
    );
  };

  const handleStop = () => {
    const request = requestRef.current;
    if (request) markIncompleteResponse(request.provider, request.messageId,
      'Stopped viewing this response. Provider work may still complete; no automatic retry was submitted.');
    if (abortRef.current) setStreamFeedback(previous => previous
      ? { ...previous, text: 'Stopped. The partial response is not complete.' } : null);
    abortRef.current?.abort();
    setIsStreaming(false);
    abortRef.current = null;
    requestRef.current = null;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const currentModeFeature = activeMode ? FEATURE_ACTIONS.find(f => f.id === activeMode) : null;
  const currentModel = isGemini ? GEMINI_MODELS.find(m => m.id === selectedModel) : null;
  const visibleFeatures = FEATURE_ACTIONS.filter(f => !f.geminiOnly || isGemini);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Provider Tabs */}
      <div className="studio-toolbar flex items-center gap-1 border-b px-3 py-2 flex-shrink-0">
        {providers.map(p => (
          <button key={p.id} onClick={() => setActiveProvider(p.id)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors ${
              activeProvider === p.id ? 'bg-[#2f2414] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/60 hover:text-[#2f2414]'
            }`}>
            <p.icon className="h-4 w-4" style={{ color: activeProvider === p.id ? p.accent : undefined }} />
            {p.name}
          </button>
        ))}
        <div className="flex-1" />

        {/* Model picker (Gemini) */}
        {isGemini && (
          <div className="relative">
            <button onClick={() => setShowModelPicker(v => !v)}
              className="flex items-center gap-1.5 rounded-lg border border-[#d6bd7c]/60 bg-[#fff8ec] px-2.5 py-1.5 text-xs font-semibold text-[#2f2414] hover:bg-[#fef3d0] transition-colors">
              {currentModel ? (
                <><currentModel.icon className="w-3.5 h-3.5 text-[#4285f4]" />{currentModel.label}</>
              ) : (
                <><Zap className="w-3.5 h-3.5 text-[#4285f4]" />Auto</>
              )}
              <ChevronDown className="w-3 h-3 opacity-60" />
            </button>
            {showModelPicker && (
              <div className="absolute right-0 top-full mt-1 w-36 rounded-xl border border-[#d6bd7c] bg-[#fff8ec] shadow-lg z-20 py-1">
                {GEMINI_MODELS.map(m => (
                  <button key={m.id} onClick={() => { setSelectedModel(m.id); setShowModelPicker(false); }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs font-medium transition-colors ${
                      selectedModel === m.id ? 'bg-[#4285f4]/10 text-[#4285f4] font-bold' : 'text-[#2f2414] hover:bg-[#e9d6aa]/50'
                    }`}>
                    <m.icon className="w-3.5 h-3.5" style={{ color: selectedModel === m.id ? '#4285f4' : undefined }} />
                    {m.label}
                    {selectedModel === m.id && <CheckCircle2 className="w-3 h-3 ml-auto text-[#4285f4]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Auth badge (Gemini) */}
        {isGemini && (
          <button onClick={() => setShowGeminiAuth(true)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors ${
              geminiAuthStatus !== 'ok'
                ? 'border-[#ea4335]/50 bg-[#ea4335]/10 text-[#b31412] hover:bg-[#ea4335]/20'
                : geminiMode === 'notebooklm-shared'
                ? 'border-[#fbbc05]/60 bg-[#fbbc05]/10 text-[#7a5200] hover:bg-[#fbbc05]/20'
                : 'border-[#34a853]/50 bg-[#34a853]/10 text-[#1e7a3c] hover:bg-[#34a853]/20'
            }`}
            title={geminiMode === 'notebooklm-shared' ? 'Using shared notebooklm account — click to switch to your personal account' : undefined}>
            {geminiAuthStatus !== 'ok'
              ? <><AlertTriangle className="w-3.5 h-3.5" />Connect</>
              : geminiMode === 'notebooklm-shared'
              ? <><AlertTriangle className="w-3.5 h-3.5" />Shared acct</>
              : <><CheckCircle2 className="w-3.5 h-3.5" />Authenticated</>}
          </button>
        )}

        <button onClick={() => {
          handleStop();
          const removed = new Set(messages.map(message => message.id));
          setProvenanceByMessage(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => !removed.has(id))));
          setStreamFeedback(null);
          clearChat(activeProvider);
        }} className="studio-button-quiet rounded-lg p-1.5" title="Clear chat" aria-label="Clear chat">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {showGeminiAuth && (
        <GeminiAuthDialog onClose={() => setShowGeminiAuth(false)} onSaved={() => {
          setGeminiAuthStatus('ok');
          setShowGeminiAuth(false);
          // Re-fetch mode so badge updates immediately
          fetch('/api/gemini/status', { headers: { Authorization: `Bearer ${localStorage.getItem('studio-token') || ''}` } })
            .then(r => r.json()).then(d => setGeminiMode(d.mode || '')).catch(() => {});
        }} />
      )}

      {/* Messages */}
      <div ref={messagesRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4 min-h-0">
        {messages.length === 0 ? (
          <WelcomeScreen provider={currentProvider} onAction={(id) => setActiveMode(prev => prev === id ? null : id)} />
        ) : (
          messages.map(msg => <div key={msg.id}>
            <MessageBubble msg={msg} />
            {provenanceByMessage[msg.id] && <div className="ml-10 max-w-[85%]">
              <FlowkitSkillProvenance provenance={provenanceByMessage[msg.id]} />
            </div>}
            {streamFeedback?.messageId === msg.id && <p role={streamFeedback.error ? 'alert' : 'status'}
              className="flowkit-skill-notice ml-10 max-w-[85%]">{streamFeedback.text}</p>}
          </div>)
        )}
      </div>

      {/* Feature mode bar */}
      <div className="studio-toolbar border-t px-3 py-1.5 flex gap-1.5 flex-wrap flex-shrink-0">
        {visibleFeatures.map(f => (
          <button key={f.id}
            onClick={() => setActiveMode(prev => prev === f.id ? null : f.id)}
            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
              activeMode === f.id
                ? 'text-white'
                : 'border border-[#d6bd7c]/60 bg-transparent text-[#6d5b3f] hover:border-[#c8942e] hover:text-[#2f2414]'
            }`}
            style={activeMode === f.id ? { backgroundColor: f.color || '#865916' } : undefined}
            title={f.label}>
            <f.icon className="w-3 h-3" style={activeMode !== f.id ? { color: f.color } : undefined} />
            {f.label}
          </button>
        ))}
      </div>

      {/* Active mode indicator */}
      {currentModeFeature && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-xs font-semibold border-t flex-shrink-0"
          style={{ backgroundColor: `${currentModeFeature.color}12`, borderColor: `${currentModeFeature.color}30`, color: currentModeFeature.color }}>
          <currentModeFeature.icon className="w-3.5 h-3.5" />
          {currentModeFeature.label} mode active
          <button onClick={() => setActiveMode(null)} className="ml-auto opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Input */}
      <div className="studio-toolbar border-t px-3 py-3 flex-shrink-0">
        {/* File attachment preview strip */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 px-1">
            {attachedFiles.map((af, idx) => (
              <div key={idx} className="group relative flex items-center gap-1.5">
                {af.preview ? (
                  <div className="relative">
                    <img src={af.preview} alt={af.name} className="h-14 w-14 rounded-xl object-cover border border-[#d6bd7c]/50" />
                    <button onClick={() => removeAttachment(idx)}
                      className="absolute -top-1 -right-1 rounded-full bg-[#2f2414] p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove">
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 rounded-lg border border-[#d6bd7c]/50 bg-[#f8f4ec] px-2 py-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-[#865916] flex-shrink-0" />
                    <span className="text-xs font-medium text-[#2f2414] max-w-[120px] truncate">{af.name}</span>
                    <button onClick={() => removeAttachment(idx)} className="ml-0.5 opacity-60 hover:opacity-100">
                      <X className="w-3 h-3 text-[#2f2414]" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end max-w-4xl mx-auto">
          {/* Hidden file input — multiple selection */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept="image/*,.pdf,.txt,.csv,.md"
            onChange={handleFileChange}
          />
          {isGemini && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`rounded-xl p-2.5 flex-shrink-0 transition-colors ${
                attachedFiles.length
                  ? 'bg-[#4285f4]/15 text-[#4285f4] border border-[#4285f4]/30'
                  : 'studio-button-quiet'
              }`}
              title={attachedFiles.length ? `${attachedFiles.length} file(s) attached` : 'Attach images or files'}
            >
              <Paperclip className="w-4 h-4" />
            </button>
          )}
          <textarea ref={textareaRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={currentModeFeature ? `${currentModeFeature.label}: describe what you want…` : `Message ${currentProvider.name}…`}
            rows={2}
            className="studio-input flex-1 resize-none rounded-xl px-4 py-3 text-sm focus:outline-none"
            style={{ maxHeight: '160px' }}
          />
          {isStreaming ? (
            <button onClick={handleStop}
              className="rounded-xl bg-[#ea4335] p-2.5 text-white hover:bg-[#c62828] transition-colors flex-shrink-0"
              title="Stop">
              <StopCircle className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSend} disabled={!input.trim() && !attachedFiles.length}
              className="studio-button-primary rounded-xl p-2.5 flex-shrink-0 disabled:opacity-40"
              title="Send (Enter)">
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
